<template>
	<div class="vue-app">
		<LoginModal 
			v-if="showLoginModal"
			ref="loginModal"
			:isVisible="showLoginModal"
			:error="loginError"	
			:key="`login-${showLoginModal}`"
			@login="handleLogin"
			@submit="handleLogin"
			@cancel="showLoginModal = false"
			@show-register="handleShowRegister"
			@resend-verification="handleResendCode"
			@confirm-verification="handleConfirmRegistration"
			@forgot-password-requested="handleForgotPasswordRequest" 
		/>
		<RegisterModal
			v-if="showRegisterModal"
			ref="registerModal"
			:isVisible="showRegisterModal"
			@register="createUser"
			@confirm="handleConfirmRegistration"
			@cancel="handleRegisterCancel"
		/>
		<ForgotPasswordModal
            v-if="showForgotPasswordModal"
            :isVisible="showForgotPasswordModal"
            :initialEmail="resetEmail"
            @cancel="showForgotPasswordModal = false"
            @success="handlePasswordResetSuccess"
        />
		<EditProfileModal
			v-if="showEditProfileModal"
			:isVisible="showEditProfileModal"
			@cancel="showEditProfileModal = false"
			@auth-update="updateAuthStatus"
			@success="showEditProfileModal = false"
		/>
		<div class="header" ref="header">
			<div class="menu" :class="{ 'is-collapsed': isMenuOverflowing }">
				<div class="menu-left">
					<!-- Hamburger Menu for Mobile -->
					<div class="mobile-menu-container">
						<button class="hamburger" @click="toggleMobileMenu" aria-label="Menu">
							<span></span>
							<span></span>
							<span></span>
						</button>
						<div class="mobile-dropdown" v-if="showMobileMenu" @click="showMobileMenu = false">
							<RouterLink class="mobile-menu-item" v-if="authStatus?.isAuthenticated" to="/about">About</RouterLink>
							<RouterLink class="mobile-menu-item" to="/welcome">Welcome</RouterLink>
							<RouterLink class="mobile-menu-item" to="/terms">Terms of Service</RouterLink>
							<RouterLink class="mobile-menu-item" v-if="authStatus?.isAuthenticated" to="/cluster">Cluster</RouterLink>
							<RouterLink class="mobile-menu-item" v-if="authStatus?.isAuthenticated && authStatus.userID === '9de8c789-ee6e-40ab-92b4-55850d2c55c3'" to="/devtools">DevTools</RouterLink>
						</div>
					</div>

					<!-- Desktop Menu -->
					<div class="desktop-menu" ref="desktopMenu">
						<RouterLink class="menu-item" v-if="authStatus?.isAuthenticated" to="/about">About</RouterLink>
						<RouterLink class="menu-item" to="/welcome">Welcome</RouterLink>
						<RouterLink class="menu-item" to="/terms">Terms of Service</RouterLink>
						<RouterLink class="menu-item" v-if="authStatus?.isAuthenticated" to="/cluster">Cluster</RouterLink>
						<RouterLink class="menu-item" v-if="authStatus?.isAuthenticated && authStatus.userID === '9de8c789-ee6e-40ab-92b4-55850d2c55c3'" to="/devtools">DevTools</RouterLink>
					</div>
				</div>
				<div class="menu-center">
					<RouterLink class="menu-item brand" to="/">AppWAFN</RouterLink>
				</div>
				<div class="menu-right">
					<div v-if="authStatus?.isAuthenticated">
						<div class="user-menu">
							<button class="login">{{ authStatus.firstName }}</button>
							<div class="dropdown-content">
								<a href="#" @click.prevent="handleSignOut">Sign Out</a>
								<a href="#" @click.prevent="handleEditProfile">Edit Profile</a>
							</div>
						</div>
					</div>
					<div v-else>
						<button class="login" @click="showLoginModal = true">Sign In</button>
					</div>
				</div>
			</div>
		</div>
		<div class="content">
			<router-view :authStatus="authStatus" />
		</div>
		<div class="footer" ref="footer">AppWAFN&copy;</div>
	</div>
</template>

<script>
	import LoginModal from './components/LoginModal.vue';
	import RegisterModal from './components/RegisterModal.vue';
	import EditProfileModal from './components/EditProfileModal.vue';
	import ForgotPasswordModal from './components/ForgotPasswordModal.vue';
	
	import GetClientAuthStatusQuery from './graphql/user/GetClientAuthStatusQuery.gql';
	import GetUserQuery from './graphql/user/GetUserQuery.gql';
	import LoginMutation from './graphql/user/LoginMutation.gql';
	import CreateUserMutation from './graphql/user/CreateUserMutation.gql';
	import VerifyUser from './graphql/user/VerifyUserMutation.gql';

	export default {
		name: 'App',
		components: {
			LoginModal,
			RegisterModal,
			EditProfileModal,
			ForgotPasswordModal
		},
		data() {
			return {
				showLoginModal: false,
                showRegisterModal: false,
                showEditProfileModal: false,
                showForgotPasswordModal: false,
				showMobileMenu: false,
				isMenuOverflowing: false,
                loginError: '',
                resetEmail: '',
				pendingUserID: ''
			};
		},
		apollo: {
			authStatus: {
				query: GetClientAuthStatusQuery,
				fetchPolicy: 'cache-only',
				result() {
					this.checkMenuOverflow();
				}
			}
		},
		methods: {
			checkMenuOverflow() {
				this.$nextTick(() => {
					const desktopMenu = this.$refs.desktopMenu;
					const header = this.$refs.header;
					if (desktopMenu && header) {
						// Measure the actual width of the menu items
						// Even when hidden via visibility:hidden, offsetWidth works
						const menuWidth = desktopMenu.offsetWidth || desktopMenu.scrollWidth;
						
						// Calculate actual available space for the left section
						// The menu is: [Left (flex:1)] [Center] [Right (flex:1)]
						const centerElement = header.querySelector('.menu-center');
						const centerWidth = centerElement ? centerElement.offsetWidth : 100;
						const availableWidth = (header.offsetWidth - centerWidth) / 2 - 30; // 30px buffer
						
						const shouldCollapse = (menuWidth > availableWidth) || (window.innerWidth < 500);
						
						if (this.isMenuOverflowing !== shouldCollapse) {
							this.isMenuOverflowing = shouldCollapse;
						}
					}
				});
			},
			toggleMobileMenu() {
				this.showMobileMenu = !this.showMobileMenu;
			},
			handleShowRegister() {
				this.loginError = '';
				this.showLoginModal = false;
				this.showRegisterModal = true;
			},
			handleRegisterCancel() {
				this.showRegisterModal = false;
			},
			async updateAuthStatus(user) {
				try {
					const hasOneVerification = !!(user?.isEmailVerified || user?.isPhoneVerified);
					// We create the exact shape the AuthStatus type expects in your cache
					const authStatus = {
						__typename: 'AuthStatus',
						ID: 'local-auth-status',
						isAuthenticated: !!user && hasOneVerification, // true if user exists and is verified
						email: user?.email || '',
						firstName: user?.firstName || '',
						familyName: user?.familyName || '',
						userID: user?.userID || '',
						phoneNumber: user?.phoneNumber || '',
						isEmailVerified: user?.isEmailVerified || false,
						isPhoneVerified: user?.isPhoneVerified || false,
						isAdmin: user?.isAdmin || false
					};

					await this.$apollo.getClient().writeQuery({
						query: GetClientAuthStatusQuery,
						data: { authStatus },
						broadcast: true
					});
					// Re-check menu width when auth status changes
					this.checkMenuOverflow();
				} catch (e) {
					console.error("Apollo cache update failed:", e);
				}
			},
			async createUser(payload) {
				const { email, password, givenName, familyName, phone_number } = payload;
				
				if (this.$refs.registerModal) {
					this.$refs.registerModal.isRegistering = true;
					this.$refs.registerModal.error = '';
				}

				try {
					const { data } = await this.$apollo.mutate({
						mutation: CreateUserMutation,
						variables: {
							email,
							password,
							firstName: givenName,
							familyName,
							phoneNumber: phone_number
						}
					});
					


					if (data && data.CreateUser) {
						// console.log('User created, moving to Step 2');
						// ONLY switch steps here. Do NOT close the modal.
						this.pendingUserID = data.CreateUser.user.userID;
						if (this.$refs.registerModal) {
							// Start with email verification
							this.$refs.registerModal.nextStep('email', email);
						}
					}

					if (data.emailCodeSent) {
					await this.openEmailVerifyDialog(data.user.userID);
					}
					if (data.phoneCodeSent) {
					await this.openPhoneVerifyDialog(data.user.userID);
					}


				} catch (error) {
					console.error('createUser failed:', error);
					if (this.$refs.registerModal) {
						this.$refs.registerModal.setError(
							error.graphQLErrors?.[0]?.message || 
							error.message || 'Registration failed.'
						);
					}
				} finally {
					if (this.$refs.registerModal) {
						this.$refs.registerModal.isRegistering = false;
					}
				}
			},
			async handleLogin(payload) {
				this.loginError = ''; // Clear previous errors

				// 1. Handle "Remember Me" credentials
				if (payload.rememberMe && payload.email && payload.password) {
					localStorage.setItem('rememberedEmail', payload.email);
					localStorage.setItem('rememberedPassword', payload.password);
				} else if (!payload.rememberMe) {
					localStorage.removeItem('rememberedEmail');
					localStorage.removeItem('rememberedPassword');
				}

				try {
					// 2. Execute the Login Mutation
					const { data } = await this.$apollo.mutate({
						mutation: LoginMutation,
						variables: {
							email: payload.email,
							password: payload.password,
							isAutoLogin: !!payload.isAutoLogin
						}
					});

					if (data && data.Login) {
						const { token, user } = data.Login;

						// 3. Save session to LocalStorage or SessionStorage based on "Remember Me"
						const storage = payload.rememberMe ? localStorage : sessionStorage;
						storage.setItem('user-token', token);
						storage.setItem('user-id', user.userID);

						// Also clear from the other storage to avoid confusion
						const otherStorage = payload.rememberMe ? sessionStorage : localStorage;
						otherStorage.removeItem('user-token');
						otherStorage.removeItem('user-id');

						// 4. Update the Apollo Cache (Menu/Header UI)
						await this.updateAuthStatus(user);

						// 5. Force UI to update immediately
						if (this.$apollo.queries.authStatus) {
							await this.$apollo.queries.authStatus.refetch();
						}

						// 6. UI Cleanup and Modal Close
						if (this.$refs.loginModal) {
							this.$refs.loginModal.reset();
						}
						this.showLoginModal = false;

						// 7. Success Redirect
						if (this.$route.name !== 'Cluster') {
							await this.$router.push({ name: 'Cluster' });
						}
					}
				} catch (error) {
					console.error('Login Error:', error);
					const errMsg = error.message;

					// --- CATCH UNCONFIRMED USERS ---
					if (errMsg.includes('AUTO_LOGIN_UNCONFIRMED')) {
						// console.log('Auto-login: User is unconfirmed. Skipping auto-login.');
						return;
					}

					if (errMsg.includes('USER_UNCONFIRMED')) {
						// console.log('Login: User is unconfirmed. Switching to verification Modal.');
						
						// Extract the userID from the server error extensions
						const userID = error.graphQLErrors?.[0]?.extensions?.userID;
						
						if (userID) {
							this.pendingUserID = userID; // Store it for the Verify mutation
							
							// Switch from Login Modal to Verification screen
							this.showLoginModal = false;
							this.showRegisterModal = true;

							// Wait for the Modal to open, then switch it to Step 2
							this.$nextTick(() => {
								if (this.$refs.registerModal) {
									this.$refs.registerModal.showVerificationStep(
										payload.email, 
										'Your account is not confirmed. Please enter the code sent to your email.'
									);
								}
							});
						} else {
							this.loginError = 'Account not confirmed, but server failed to provide verification ID.';
						}
						return; // Stop here, we've switched to the verification flow
					}

					// --- HANDLE STANDARD LOGIN ERRORS ---
					if (errMsg.includes('No such user') || errMsg.includes('Invalid password')) {
						this.loginError = 'Invalid email or password.';
					} else {
						this.loginError = error.graphQLErrors?.[0]?.message || errMsg || 'An error occurred during sign in.';
					}
				}
			},
			async handleConfirmRegistration(payload) {
				const { code, type } = payload;
				
				// 1. Identify who we are verifying
				// We use pendingUserID (from Register/Login) OR authStatus.userID (from Edit Profile)
				const userID = this.pendingUserID || (this.authStatus && this.authStatus.userID);

				if (this.$refs.registerModal) {
					this.$refs.registerModal.isConfirming = true;
					this.$refs.registerModal.error = '';
				}

				// Safety check: if we have no ID, we can't verify
				if (!userID) {
					console.error("Verification failed: No userID found in state.");
					if (this.$refs.registerModal) {
						this.$refs.registerModal.setError("Session error. Please try logging in again to receive a new code.");
					}
					return;
				}

				try {
					// 2. Call the single VerifyUser mutation
					const { data } = await this.$apollo.mutate({
						mutation: VerifyUser,
						variables: { 
							userID: userID,
							code: code,
							type: type
						}
					});

					if (data && data.VerifyUser) {
						const { token, user } = data.VerifyUser;

						// Check if phone verification is needed after email
						if (type === 'email' && user.phoneNumber && !user.isPhoneVerified) {
							// console.log('Email verified, moving to Phone verification');
							if (this.$refs.registerModal) {
								this.$refs.registerModal.isConfirming = false;
								this.$refs.registerModal.nextStep('phone', user.phoneNumber);
							}
							return;
						}

						// Also check if we just verified phone but email is still pending
						if (type === 'phone' && user.email && !user.isEmailVerified) {
							// console.log('Phone verified, moving to Email verification');
							if (this.$refs.registerModal) {
								this.$refs.registerModal.isConfirming = false;
								this.$refs.registerModal.nextStep('email', user.email);
							}
							return;
						}

						// 3. Save the session data (Verification usually implies staying logged in for this session)
						// We check if "rememberedEmail" exists to decide if we use localStorage
						const usePersistent = !!localStorage.getItem('rememberedEmail');
						const storage = usePersistent ? localStorage : sessionStorage;
						
						storage.setItem('user-token', token);
						storage.setItem('user-id', user.userID);
						
						const otherStorage = usePersistent ? sessionStorage : localStorage;
						otherStorage.removeItem('user-token');
						otherStorage.removeItem('user-id');

						// 4. Update the Reactive UI (Apollo Cache)
						await this.updateAuthStatus(user);

						// 5. Force the Menu/Header to update if reactivity is slow
						if (this.$apollo.queries.authStatus) {
							await this.$apollo.queries.authStatus.refetch();
						}

						// 6. UI Cleanup: Close everything and clear pending state
						this.showRegisterModal = false;
						this.showEditProfileModal = false;
						this.pendingUserID = ''; 

						if (this.$refs.registerModal) {
							this.$refs.registerModal.reset();
						}

						// 7. Success Redirect
						if (this.$route.name !== 'Cluster') {
							await this.$router.push({ name: 'Cluster' });
						}
						
						// console.log('User successfully verified and logged in.');
					}
				} catch (error) {
					console.error('Verification mutation failed:', error);
					if (this.$refs.registerModal) {
						this.$refs.registerModal.setError(
							error.graphQLErrors?.[0]?.message || 
							error.message || 
							'Invalid verification code.'
						);
					}
				} finally {
					if (this.$refs.registerModal) {
						this.$refs.registerModal.isConfirming = false;
					}
				}
			},
			async handleSignOut() {
				try {
					// console.log('Starting Sign Out process...');

			// 1. Clear the security token from storage
			localStorage.removeItem('user-token');
			localStorage.removeItem('user-id');
			sessionStorage.removeItem('user-token');
			sessionStorage.removeItem('user-id');

					// 2. Reset the local Auth Status in Apollo Cache
					await this.updateAuthStatus(null);

					// 3. ALWAYS clear the Apollo Store on explicit sign out
					// This ensures no user data leaks between sessions
					await this.$apollo.getClient().clearStore();

					// 4. Clear the persisted cache from localStorage
					localStorage.removeItem('apollo-cache-persistence');

			// 4. Also clear "Remember Me" credentials when explicitly signing out
			localStorage.removeItem('rememberedEmail');
			localStorage.removeItem('rememberedPassword');


					// 5. Navigate back to Home
					if (this.$route.name !== 'Home') {
						await this.$router.push({ name: 'Home' });
					}

					// console.log('Sign Out complete. Cache cleared and redirected.');
				} catch (error) {
					console.error('Error during sign out:', error);
					// Fallback: Force a redirect even if the network call fails
					this.$router.push({ name: 'Home' });
				}
			},
			async handleVerifyCode(payload) {
				const { code } = payload;
				const userID = this.pendingUserID || this.authStatus.userID;

				try {
					const { data } = await this.$apollo.mutate({
						mutation: VerifyUser,
						variables: { userID, code }
					});

					if (data && data.VerifyUser) {
						const { token, user } = data.VerifyUser;
						localStorage.setItem('user-token', token);
						localStorage.setItem('user-id', user.userID);

						await this.updateAuthStatus(user);
						
						// Close whichever modal is open
						this.showEditProfileModal - false;
						this.showRegisterModal = false;
						this.showEditProfileModal = false;
						this.pendingUserID = ''; // Clear it
						
						this.$router.push({ name: 'Cluster' });
					}
				} catch (error) {
					// ... error handling ...
				}
			},
			handleEditProfile() {
				this.showEditProfileModal = true;
			},
			handleForgotPasswordRequest(payload) {
				// console.log('App.vue: Switching to ForgotPasswordModal for', payload.email);
				
				// 1. Capture the email so the user doesn't have to type it again
				this.resetEmail = payload.email;
				
				// 2. Hide Login, Show Reset
				this.showLoginModal = false;
				this.showForgotPasswordModal = true;
				// console.log("App.vue: Login hidden, ForgotPassword shown for", this.resetEmail);
			},
			// This is called when ForgotPasswordModal says "Password changed!"
			handlePasswordResetSuccess() {
				this.showForgotPasswordModal = false;
				this.showLoginModal = true;
				// Optional: Set a success message to show on the login screen
				this.loginError = 'Password successfully updated. Please sign in.';
			},
			async handleResendCode() {
				// try {
				// 	await resendSignUpCode({ username: email });
				// 	// You could notify the user here that a new code is on the way
				// } catch (error) {
				// 	this.$refs.registerModal.setError("Failed to resend code: " + error.message);
				// }
			},
			async persistCacheForRememberedUsers() {
				// Only persist cache if:
				// 1. User has chosen "Remember Me" AND
				// 2. User is currently authenticated
				const rememberedEmail = localStorage.getItem('rememberedEmail');
				const rememberedPassword = localStorage.getItem('rememberedPassword');
				const isAuthenticated = this.authStatus?.isAuthenticated;

				if (rememberedEmail && rememberedPassword && isAuthenticated) {
					try {
						// Get the current cache data
						const cacheData = this.$apollo.getClient().cache.extract();
						// Save to localStorage for restoration on next app start
						localStorage.setItem('apollo-cache-persistence', JSON.stringify(cacheData));
						console.log('Cache persisted for remembered user');
					} catch (error) {
						console.warn('Failed to persist Apollo cache:', error);
					}
				} else {
					// If user is not authenticated, clear any persisted cache
					localStorage.removeItem('apollo-cache-persistence');
					console.log('Cleared persisted cache - user not authenticated');
				}
			},
		},
		async created() {
			// Add event listener to persist cache when page is about to unload
			window.addEventListener('beforeunload', this.persistCacheForRememberedUsers);
			// 1. Try to get token from session first, then local storage
			let token = sessionStorage.getItem('user-token') || localStorage.getItem('user-token');
			let userID = sessionStorage.getItem('user-id') || localStorage.getItem('user-id');
			
			// 1. If we have a token, try to refresh the session
			if (token && userID) {
				try {
					const { data } = await this.$apollo.query({
						query: GetUserQuery,
						variables: { userID },
						fetchPolicy: 'network-only'
					});

					if (data && data.User) {
						if (!data.User.isEmailVerified && !data.User.isPhoneVerified) { 
							console.warn('Session valid but user is not verified. Clearing...');
							localStorage.removeItem('user-token');
							localStorage.removeItem('user-id');
							sessionStorage.removeItem('user-token');
							sessionStorage.removeItem('user-id');	
						} else {
							await this.updateAuthStatus(data.User);
							return; // Success! We are logged in.
						}
					}
				} catch (error) {
					// If the token is invalid, the server will log the warning,
					// and we should clean up the local storage.
					console.warn('Session token invalid. Clearing...');
					localStorage.removeItem('user-token');
					localStorage.removeItem('user-id');
					sessionStorage.removeItem('user-token');
					sessionStorage.removeItem('user-id');
				}
			}

			// 2. Fallback: If no token (or token was bad), try "Remember Me"
			const rememberedEmail = localStorage.getItem('rememberedEmail');
			const rememberedPassword = localStorage.getItem('rememberedPassword');

			if (rememberedEmail && rememberedPassword) {
				// console.log('Attempting login with remembered credentials...');
				// Clear any existing cache before logging in a new user
				await this.$apollo.getClient().clearStore();
				localStorage.removeItem('apollo-cache-persistence');

				await this.handleLogin({ 
					email: rememberedEmail, 
					password: rememberedPassword, 
					rememberMe: true,
					isAutoLogin: true
				});
			}
		},
		mounted() {
			this.checkMenuOverflow();
			window.addEventListener('resize', this.checkMenuOverflow);
		},
		beforeUnmount() {
			window.removeEventListener('resize', this.checkMenuOverflow);
			window.removeEventListener('beforeunload', this.persistCacheForRememberedUsers);
		},
	}
</script>

<style>
	html, body {
		margin: 0;
		padding: 0;
		height: 100%;
		overflow: hidden; /* Prevents scrollbar on the body */
	}

	.vue-app {
		height: 100vh;
		display: flex;
		flex-direction: column;
		overflow: hidden;
	}

	.header {
		height: 30px;
		background-color: #a6f4e5;
		display: flex;
		align-items: center;
	}

	.footer {
		height: 20px;
		background-color: #a6f4e5;
		display: flex;
		justify-content: center;
		align-items: center;
	}

	.content {
		flex: 1;
		background: #efefef;
		overflow: auto;
	}

	.menu {
		display: flex;
		justify-content: space-between;
		align-items: center;
		width: 100%;
		padding: 0 10px;
	}

	.menu-left, .menu-right {
		flex: 1;
		display: flex;
		align-items: center;
		gap: 10px;
	}

	.menu-right {
		justify-content: flex-end;
	}

	.menu-center {
		flex: 0 0 auto;
		text-align: center;
	}

	/* Mobile Menu Styles */
	.hamburger {
		display: none;
		flex-direction: column;
		justify-content: space-around;
		width: 20px;
		height: 18px;
		background: transparent;
		border: none;
		cursor: pointer;
		padding: 0;
		z-index: 10;
	}

	.hamburger span {
		width: 20px;
		height: 2px;
		background: #2c3e50;
		border-radius: 10px;
	}

	.mobile-menu-container {
		position: relative;
		display: none;
	}

	.menu.is-collapsed .mobile-menu-container,
	.menu.is-collapsed .hamburger {
		display: flex;
	}

	.menu.is-collapsed .desktop-menu {
		visibility: hidden;
		position: absolute;
		pointer-events: none;
	}

	.mobile-dropdown {
		position: absolute;
		top: 25px;
		left: 0;
		background-color: #fff;
		min-width: 180px;
		box-shadow: 0px 8px 16px 0px rgba(0,0,0,0.2);
		z-index: 100;
		border-radius: 4px;
		display: flex;
		flex-direction: column;
		padding: 5px 0;
	}

	.mobile-menu-item {
		padding: 12px 16px;
		text-decoration: none;
		color: #2c3e50;
		font-size: 14px;
		border-bottom: 1px solid #eee;
	}

	.mobile-menu-item:last-child {
		border-bottom: none;
	}

	.mobile-menu-item:hover {
		background-color: #f5f5f5;
	}

	.desktop-menu {
		display: flex;
		gap: 10px;
		white-space: nowrap;
	}

	/* Responsive adjustments - Keep basic mobile safety */
	@media (max-width: 800px) {
		.menu-left, .menu-right {
			flex: 1;
		}
		.brand {
			font-size: 14px;
		}
	}

	.menu-item {
		padding: 2px 8px;
		text-decoration: none;
		color: #2c3e50;
		border: 1px solid transparent;
		border-radius: 4px;
		font-size: 13px;
	}

	.menu-item:hover {
		background-color: rgba(255, 255, 255, 0.3);
		border-color: #888;
	}

	.brand {
		font-weight: bold;
		font-size: 16px;
		letter-spacing: 1px;
	}

	.menu-item:hover {
		border-color: #888;
	}

	.login {
		background: none;
		border: 1px solid transparent;
		border-radius: 4px;
		cursor: pointer;
		text-decoration: none;
		font-family: inherit;
		font-size: inherit;
		color: inherit;
		padding: 2px 5px;
	}

	.login:hover {
		border-color: #888;
	}

	.user-menu {
		position: relative;
		display: inline-block;
	}

	.dropdown-content {
		display: none;
		position: absolute;
		background-color: #f1f1f1;
		min-width: 160px;
		box-shadow: 0px 8px 16px 0px rgba(0,0,0,0.2);
		z-index: 1;
		right: 0;
	}

	.dropdown-content a {
		color: black;
		padding: 12px 16px;
		text-decoration: none;
		display: block;
	}

	.dropdown-content a:hover {background-color: #ddd;}

	.user-menu:hover .dropdown-content {display: block;}
</style>
